import { defineStore } from 'pinia'
import { computed, ref, watch, type Ref, type WritableComputedRef } from 'vue'
import type { PageNamingScheme } from '../../services/pages/pageNaming'
import {
  cloneDocumentTemplate,
  createBlankDocumentTemplate,
  defaultDocumentTemplates,
  normalizeDocumentTemplate,
  type DocumentTemplate,
} from '../../services/corpus/documentTemplates'
import { setListPrefsSource } from '../../utils/listStyles'
import { IS_MOBILE_APP } from '../../utils/appVariant'
import { removeBarKey, restoreBarKey } from '../../utils/barLayout'
import { defaultSettings, loadPersisted, readSettings, type SettingsValues } from './load'
import { useSettingsSync } from './sync'
import { useBackupSettings } from './backup'
import { defaultIconStyles } from './iconStyles'
import { defaultContextMenuItems } from './contextMenu'

// The writer's settings. Each domain's types, defaults and cleanup live
// beside this file; load.ts reads them at startup (carrying over older
// saves) and sync.ts saves them and keeps them in step with the cloud.

export { DEFAULT_LOOSE_END_SETTINGS, normalizeLooseEndSettings, type LooseEndSettings } from './looseEnds'
export { DEFAULT_EDITOR_MODE_SETTINGS, type EditorModePalette } from './editorMode'
export { PROOFING_DIALECTS, normalizeProofingSettings, type ProofingDialect, type ProofingSettings } from './proofing'
export { defaultBackupSettings, type BackupSettings, type BackupTier } from './backup'
export { contextMenuItemCatalog, contextMenuContexts } from './contextMenu'
export type { ShortcutOverrides } from './normalize'

// A folder on this computer, so it is kept per device and never synced.
const MARKDOWN_MIRROR_STORAGE_KEY = 'ink-markdown-mirror-path'

/** A ref that reads and writes the desktop or the mobile copy, for the app in hand. */
function perDevice<T>(desktop: Ref<T>, mobile: Ref<T>): WritableComputedRef<T> {
  const active = IS_MOBILE_APP ? mobile : desktop
  return computed({
    get: () => active.value,
    set: (value: T) => { active.value = value },
  })
}

/** Switching a width's unit converts the width, so the column stays about the same size. */
function convertWidthWithUnit(unit: Ref<'px' | 'ch'>, width: Ref<number>) {
  watch(unit, (newUnit, oldUnit) => {
    if (newUnit === oldUnit) return
    if (newUnit === 'ch') {
      if (width.value > 200) {
        width.value = Math.min(150, Math.max(30, Math.round(width.value / 10)))
      } else if (width.value < 20) {
        width.value = 65
      }
    } else if (newUnit === 'px') {
      if (width.value <= 200) {
        const maxPx = typeof window !== 'undefined' ? window.innerWidth : 1600
        width.value = Math.min(maxPx, Math.max(300, Math.round(width.value * 10)))
      } else if (width.value < 200) {
        width.value = 700
      }
    }
  })
}

type SettingRefs = { [K in keyof SettingsValues]: Ref<SettingsValues[K]> }

export const useSettingsStore = defineStore('settings', () => {
  const persisted = loadPersisted()
  const backupSettings = useBackupSettings()

  // Older saves kept the mirror folder with the synced settings.
  const markdownMirrorPath = ref<string | null>(localStorage.getItem(MARKDOWN_MIRROR_STORAGE_KEY) || persisted.markdownMirrorPath || null)
  watch(markdownMirrorPath, (value) => {
    if (value) localStorage.setItem(MARKDOWN_MIRROR_STORAGE_KEY, value)
    else localStorage.removeItem(MARKDOWN_MIRROR_STORAGE_KEY)
  }, { immediate: true })

  // Every synced setting, one ref each. This is the one list of them: it is
  // what is saved, synced, exported and imported.
  const initial = readSettings(persisted)
  const synced = Object.fromEntries(
    Object.entries(initial).map(([key, value]) => [key, ref(value)]),
  ) as SettingRefs
  const {
    desktopWidth, desktopWidthUnit, mobileWidth, mobileWidthUnit,
    desktopFontSize, mobileFontSize,
    desktopTypewriterModeEnabled, mobileTypewriterModeEnabled,
    desktopCorpusView, mobileCorpusView,
    mobileSelectionToolbarEnabled, radialMenuEnabled,
    listPrefs, desktopBarLayout, iconStyles, contextMenuItems,
    pageNamingSchemes, pageNamingAssignments, documentTemplates,
  } = synced

  // Text size, text width, typewriter mode and the corpus view are kept per
  // device: a phone in the hand and a monitor at arm's length want different
  // values (and the phone can't show every corpus view). Each has a
  // desktop and a mobile copy (both synced), and the plain name reads and
  // writes the copy for the app in hand.
  convertWidthWithUnit(desktopWidthUnit, desktopWidth)
  convertWidthWithUnit(mobileWidthUnit, mobileWidth)
  const widthUnit = perDevice(desktopWidthUnit, mobileWidthUnit)
  const width = perDevice(desktopWidth, mobileWidth)
  const fontSize = perDevice(desktopFontSize, mobileFontSize)
  const typewriterModeEnabled = perDevice(desktopTypewriterModeEnabled, mobileTypewriterModeEnabled)
  const corpusView = perDevice(desktopCorpusView, mobileCorpusView)

  setListPrefsSource(() => listPrefs.value)

  // The selection menu and the radial menu share the long press, so only one
  // can be on at a time.
  watch(mobileSelectionToolbarEnabled, (on) => {
    if (on && radialMenuEnabled.value) radialMenuEnabled.value = false
  })
  watch(radialMenuEnabled, (on) => {
    if (on && mobileSelectionToolbarEnabled.value) mobileSelectionToolbarEnabled.value = false
  })

  const { applyRemoteSettings, syncSettingsWithCloud, exportableSettings, applyImportedSettings } =
    useSettingsSync(synced, synced.syncSettingsEnabled, defaultSettings())

  /** Whether a key is on a desktop bar. */
  function isBarKeyOn(barId: string, keyId: string) {
    return desktopBarLayout.value.bars[barId]?.includes(keyId) ?? false
  }

  /** Puts a key on a desktop bar where it last sat, or takes it off. */
  function setBarKeyOn(barId: string, keyId: string, on: boolean) {
    desktopBarLayout.value = on
      ? restoreBarKey(desktopBarLayout.value, 'desktop', barId, keyId)
      : removeBarKey(desktopBarLayout.value, barId, keyId)
  }

  function resetIconStyles() {
    iconStyles.value = JSON.parse(JSON.stringify(defaultIconStyles))
  }

  function resetContextMenuItems(contextId?: string) {
    if (contextId) {
      contextMenuItems.value = {
        ...contextMenuItems.value,
        [contextId]: [...(defaultContextMenuItems[contextId] || [])],
      }
    } else {
      contextMenuItems.value = JSON.parse(JSON.stringify(defaultContextMenuItems))
    }
  }

  function addPageNamingScheme(name: string, template: string, restartPerFolder = false): PageNamingScheme {
    const scheme: PageNamingScheme = {
      id: globalThis.crypto?.randomUUID?.() ?? `scheme-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      name: name.trim(),
      template: template.trim(),
      restartPerFolder,
    }
    pageNamingSchemes.value = [...pageNamingSchemes.value, scheme]
    return scheme
  }

  function deletePageNamingScheme(id: string) {
    pageNamingSchemes.value = pageNamingSchemes.value.filter(scheme => scheme.id !== id)
    pageNamingAssignments.value = Object.fromEntries(
      Object.entries(pageNamingAssignments.value).filter(([, schemeId]) => schemeId !== id)
    )
  }

  function setPageNamingSchemeRestart(id: string, value: boolean) {
    pageNamingSchemes.value = pageNamingSchemes.value.map(scheme =>
    scheme.id === id ? { ...scheme, restartPerFolder: value } : scheme
    )
  }

  function setContainerNamingScheme(containerId: string, schemeId: string | null) {
    const next = { ...pageNamingAssignments.value }
    if (schemeId) next[containerId] = schemeId
    else delete next[containerId]
    pageNamingAssignments.value = next
  }

  function saveDocumentTemplate(template: DocumentTemplate): DocumentTemplate | null {
    const normalized = normalizeDocumentTemplate(template)
    if (!normalized) return null
    const index = documentTemplates.value.findIndex(item => item.id === normalized.id)
    if (index === -1) documentTemplates.value = [...documentTemplates.value, normalized]
    else documentTemplates.value = documentTemplates.value.map(item => item.id === normalized.id ? normalized : item)
    return normalized
  }

  function addDocumentTemplate(source?: DocumentTemplate): DocumentTemplate {
    const template = source ? cloneDocumentTemplate(source) : createBlankDocumentTemplate()
    if (source) {
      template.id = `template_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`
      template.name = `${source.name} Copy`
      template.preset = false
    }
    documentTemplates.value = [...documentTemplates.value, template]
    return template
  }

  function deleteDocumentTemplate(id: string) {
    documentTemplates.value = documentTemplates.value.filter(template => template.id !== id)
  }

  function resetDocumentTemplate(id: string): DocumentTemplate | null {
    const preset = defaultDocumentTemplates.find(template => template.id === id)
    if (!preset) return null
    return saveDocumentTemplate(cloneDocumentTemplate(preset))
  }

  function restoreMissingDocumentTemplates() {
    const existing = new Set(documentTemplates.value.map(template => template.id))
    const missing = defaultDocumentTemplates
      .filter(template => !existing.has(template.id))
      .map(cloneDocumentTemplate)
    if (missing.length) documentTemplates.value = [...documentTemplates.value, ...missing]
  }

  return {
    ...synced,
    width,
    widthUnit,
    fontSize,
    typewriterModeEnabled,
    corpusView,
    backupSettings,
    markdownMirrorPath,
    exportableSettings,
    applyImportedSettings,
    applyRemoteSettings,
    syncSettingsWithCloud,
    isBarKeyOn,
    setBarKeyOn,
    resetContextMenuItems,
    addPageNamingScheme,
    deletePageNamingScheme,
    setPageNamingSchemeRestart,
    setContainerNamingScheme,
    saveDocumentTemplate,
    addDocumentTemplate,
    deleteDocumentTemplate,
    resetDocumentTemplate,
    restoreMissingDocumentTemplates,
    resetIconStyles,
  }
})
